import { useFetchFeatureConfig } from "./useFetchFeatureConfig";

import type { FeaturesConfigKey } from "../constants/feature";

export const FEATURE_ACCESS = {
  LOADING: "loading",
  ENABLED: "enabled",
  DISABLED: "disabled",
  ERROR: "error",
} as const;

export type FeatureAccess =
  (typeof FEATURE_ACCESS)[keyof typeof FEATURE_ACCESS];

/**
 * Resolves a feature flag for the current organization into one of four states.
 *
 * - `LOADING` — the feature config request is still in flight
 * - `ENABLED` — the flag came back explicitly `true`
 * - `DISABLED` — the flag came back `false`, or the config carried no entry for it
 * - `ERROR` — the config request failed and no earlier config is cached
 *
 * Only an explicit `true` enables a feature. A flag that was never seeded for the
 * organization resolves to `DISABLED`, so it stays shut rather than opening to everyone.
 *
 * @param feature - The feature config key to resolve
 * @returns The access state for the current organization
 */
export const useFeatureAccess = (feature: FeaturesConfigKey): FeatureAccess => {
  const { data: featureConfig, isLoading, isError } = useFetchFeatureConfig();

  if (isLoading) return FEATURE_ACCESS.LOADING;
  if (isError && !featureConfig) return FEATURE_ACCESS.ERROR;
  if (featureConfig?.[feature] === true) return FEATURE_ACCESS.ENABLED;

  return FEATURE_ACCESS.DISABLED;
};
